const toneMarkMap: Record<string, string[]> = {
  a: ['a', 'ā', 'á', 'ǎ', 'à', 'a'],
  e: ['e', 'ē', 'é', 'ě', 'è', 'e'],
  i: ['i', 'ī', 'í', 'ǐ', 'ì', 'i'],
  o: ['o', 'ō', 'ó', 'ǒ', 'ò', 'o'],
  u: ['u', 'ū', 'ú', 'ǔ', 'ù', 'u'],
  ü: ['ü', 'ǖ', 'ǘ', 'ǚ', 'ǜ', 'ü']
}

const zhuyinInitials: Record<string, string> = {
  b: 'ㄅ',
  p: 'ㄆ',
  m: 'ㄇ',
  f: 'ㄈ',
  d: 'ㄉ',
  t: 'ㄊ',
  n: 'ㄋ',
  l: 'ㄌ',
  g: 'ㄍ',
  k: 'ㄎ',
  h: 'ㄏ',
  j: 'ㄐ',
  q: 'ㄑ',
  x: 'ㄒ',
  zh: 'ㄓ',
  ch: 'ㄔ',
  sh: 'ㄕ',
  r: 'ㄖ',
  z: 'ㄗ',
  c: 'ㄘ',
  s: 'ㄙ'
}

const zhuyinFinals: Record<string, string> = {
  a: 'ㄚ',
  o: 'ㄛ',
  e: 'ㄜ',
  ai: 'ㄞ',
  ei: 'ㄟ',
  ao: 'ㄠ',
  ou: 'ㄡ',
  an: 'ㄢ',
  ang: 'ㄤ',
  en: 'ㄣ',
  eng: 'ㄥ',
  er: 'ㄦ',
  i: 'ㄧ',
  ia: 'ㄧㄚ',
  ie: 'ㄧㄝ',
  iao: 'ㄧㄠ',
  iu: 'ㄧㄡ',
  ian: 'ㄧㄢ',
  iang: 'ㄧㄤ',
  in: 'ㄧㄣ',
  ing: 'ㄧㄥ',
  iong: 'ㄩㄥ',
  u: 'ㄨ',
  ua: 'ㄨㄚ',
  uo: 'ㄨㄛ',
  uai: 'ㄨㄞ',
  ui: 'ㄨㄟ',
  uan: 'ㄨㄢ',
  uang: 'ㄨㄤ',
  un: 'ㄨㄣ',
  ong: 'ㄨㄥ',
  ueng: 'ㄨㄥ',
  ü: 'ㄩ',
  üe: 'ㄩㄝ',
  üan: 'ㄩㄢ',
  ün: 'ㄩㄣ'
}

const zhuyinIndividuals: Record<string, string> = {
  zhi: 'ㄓ',
  chi: 'ㄔ',
  shi: 'ㄕ',
  ri: 'ㄖ',
  zi: 'ㄗ',
  ci: 'ㄘ',
  si: 'ㄙ',
  a: 'ㄚ',
  o: 'ㄛ',
  e: 'ㄜ',
  ai: 'ㄞ',
  ei: 'ㄟ',
  ao: 'ㄠ',
  ou: 'ㄡ',
  an: 'ㄢ',
  ang: 'ㄤ',
  en: 'ㄣ',
  eng: 'ㄥ',
  er: 'ㄦ',
  hm: 'ㄏㄇ',
  hng: 'ㄏㄫ',
  m: 'ㄇ',
  n: 'ㄋ',
  ng: 'ㄫ',
  wu: 'ㄨ',
  wa: 'ㄨㄚ',
  wo: 'ㄨㄛ',
  wai: 'ㄨㄞ',
  wei: 'ㄨㄟ',
  wan: 'ㄨㄢ',
  wang: 'ㄨㄤ',
  wen: 'ㄨㄣ',
  weng: 'ㄨㄥ',
  yi: 'ㄧ',
  ya: 'ㄧㄚ',
  ye: 'ㄧㄝ',
  yao: 'ㄧㄠ',
  you: 'ㄧㄡ',
  yan: 'ㄧㄢ',
  yang: 'ㄧㄤ',
  yin: 'ㄧㄣ',
  ying: 'ㄧㄥ',
  yu: 'ㄩ',
  yue: 'ㄩㄝ',
  yuan: 'ㄩㄢ',
  yun: 'ㄩㄣ',
  yong: 'ㄩㄥ'
}

const toneSymbols: Record<number, string> = {
  1: '',
  2: 'ˊ',
  3: 'ˇ',
  4: 'ˋ',
  5: '˙'
}

const initialKeys = Object.keys(zhuyinInitials).sort(
  (a, b) => b.length - a.length
)

export function normalizePinyinNumber(value: string) {
  return value.trim().toLowerCase().replace(/^_/, '').replaceAll('v', 'ü')
}

export function getToneFromPinyinNumber(value: string) {
  const match = normalizePinyinNumber(value).match(/([1-5])$/)
  return match ? Number(match[1]) : 5
}

export function getBaseFromPinyinNumber(value: string) {
  return normalizePinyinNumber(value).replace(/[1-5]$/, '')
}

export function pinyinNumberToDisplay(value: string) {
  const normalized = normalizePinyinNumber(value)
  const tone = getToneFromPinyinNumber(normalized)
  const base = getDisplayBase(getBaseFromPinyinNumber(normalized))
  if (tone === 5) return base

  const targetIndex = getToneMarkIndex(base)
  if (targetIndex < 0) return base

  const chars = Array.from(base)
  const target = chars[targetIndex]
  const marked = toneMarkMap[target]?.[tone] ?? target
  chars[targetIndex] = marked
  return chars.join('')
}

function getDisplayBase(base: string) {
  if (/^[jqx]ü/.test(base)) {
    return `${base.slice(0, 1)}u${base.slice(2)}`
  }
  return base
}

export function pinyinTextNumberToDisplay(value: string) {
  return tokenizePinyinNumber(value)
    .map((token) => pinyinNumberToDisplay(token))
    .join(' ')
}

export function pinyinSyllableToZhuyin(value: string) {
  const normalized = normalizePinyinNumber(value)
  const tone = getToneFromPinyinNumber(normalized)
  const base = getBaseFromPinyinNumber(normalized)
  const individual = zhuyinIndividuals[base]

  let zhuyin = ''
  if (individual) {
    zhuyin = individual
  } else {
    const initial = initialKeys.find((candidate) => base.startsWith(candidate))
    if (!initial) {
      zhuyin = base
    } else {
      let final = base.slice(initial.length)
      if (['j', 'q', 'x'].includes(initial) && final.startsWith('u')) {
        final = `ü${final.slice(1)}`
      }
      const zhuyinFinal = zhuyinFinals[final]
      if (!zhuyinFinal) {
        zhuyin = base
      } else {
        zhuyin = `${zhuyinInitials[initial]}${zhuyinFinal}`
      }
    }
  }

  if (tone === 5) {
    return `˙${zhuyin}`
  }
  return `${zhuyin}${toneSymbols[tone] ?? ''}`
}

export function pinyinTextNumberToZhuyin(value: string) {
  return tokenizePinyinNumber(value)
    .map((token) => pinyinSyllableToZhuyin(token))
    .join(' ')
}

export function tokenizePinyinNumber(value: string) {
  return value
    .split(/[\s']+/)
    .map((token) => token.trim())
    .filter(Boolean)
}

function getToneMarkIndex(base: string) {
  const chars = Array.from(base)
  const aIndex = chars.indexOf('a')
  if (aIndex >= 0) return aIndex
  const eIndex = chars.indexOf('e')
  if (eIndex >= 0) return eIndex
  const ouIndex = base.indexOf('ou')
  if (ouIndex >= 0) return Array.from(base.slice(0, ouIndex)).length

  for (let index = chars.length - 1; index >= 0; index -= 1) {
    if (['i', 'o', 'u', 'ü'].includes(chars[index])) return index
  }
  return -1
}
